<template>
  <div class="asc-grid-pagination">
    <div class="asc-grid-pagination--container">
      <div v-if="showRowsPerPage" class="asc-paging-page-size-panel">
        <span class="asc-paging-page-size--label">Rows Per Page</span>
        <asc-autocomplete-selector
          v-model="pageSizeSelected"
          :items="pageSizes"
          no-array
          no-search-input
          no-label
          no-tooltip
          dense
          drop-down-size-matches
          :sort-items="false"
        ></asc-autocomplete-selector>
      </div>
      <div class="asc-paging-row-summary-panel">
        <span>{{ getFirstRowOnPage }}</span>
        to
        <span>{{ getLastRowOnPage }}</span>
        of
        <span>{{ getRecordCount }}</span>
      </div>
      <div class="asc-paging-page-summary-panel">
        <div class="asc-paging-button asc-disabled">
          <v-btn
            x-small
            icon
            :disabled="isFirstPageDisabled"
            @click="handleFirstPage"
          >
            <v-icon size="20">mdi-chevron-double-left</v-icon>
          </v-btn>
        </div>
        <div class="asc-paging-button asc-disabled">
          <v-btn
            x-small
            icon
            :disabled="isFirstPageDisabled"
            @click="handlePreviousPage"
          >
            <v-icon size="20">mdi-chevron-left</v-icon>
          </v-btn>
        </div>
        <span class="asc-paging-page-summary">
          Page {{ getCurrentPage }} of {{ getTotalPages }}
        </span>
        <div class="asc-paging-button">
          <v-btn
            x-small
            icon
            :disabled="isLastPageDisabled"
            @click="handleNextPage"
          >
            <v-icon size="20">mdi-chevron-right</v-icon>
          </v-btn>
        </div>
        <div class="asc-paging-button">
          <v-btn
            x-small
            icon
            :disabled="isLastPageDisabled"
            @click="handleLastPage"
          >
            <v-icon size="20">mdi-chevron-double-right</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AscAutocompleteSelector from './AscAutocompleteSelector'

export default {
  name: 'AscGridPagination',

  components: {
    AscAutocompleteSelector,
  },

  props: {
    gridApi: {
      type: Object,
      default: () => {},
    },
    selectedPageSizeOverride: {
      type: Number,
      default: 25,
    },
    pageSizesOverride: {
      type: Array,
      default: () => [],
    },
    showRowsPerPage: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      pageSizeSelected: this.selectedPageSizeOverride
        ? this.selectedPageSizeOverride
        : 25,
      pageSizes: this.pageSizesOverride
        ? this.pageSizesOverride
        : [
            { value: 12, text: '12' },
            { value: 25, text: '25' },
            { value: 50, text: '50' },
            { value: 100, text: '100' },
          ],
    }
  },

  computed: {
    getFirstRowOnPage() {
      if (this.gridApi) {
        const currentPage = this.gridApi.paginationGetCurrentPage()
        const pageSize = this.gridApi.paginationGetPageSize()
        return currentPage * pageSize + 1
      }
      return '0'
    },

    getLastRowOnPage() {
      if (this.gridApi) {
        const currentPage = this.gridApi.paginationGetCurrentPage() + 1
        const pageSize = this.gridApi.paginationGetPageSize()
        const rowCount = this.gridApi.paginationGetRowCount()
        const lastRow = currentPage * pageSize
        return lastRow > rowCount ? rowCount : lastRow
      }
      return '0'
    },

    getRecordCount() {
      if (this.gridApi) {
        return this.gridApi.paginationGetRowCount()
      }
      return '0'
    },

    getCurrentPage() {
      if (this.gridApi) {
        return this.gridApi.paginationGetCurrentPage() + 1
      }
      return '0'
    },

    getTotalPages() {
      if (this.gridApi) {
        return this.gridApi.paginationGetTotalPages()
      }
      return '0'
    },

    isFirstPageDisabled() {
      if (this.gridApi) {
        return this.gridApi.paginationGetCurrentPage() === 0
      }
      return true
    },

    isLastPageDisabled() {
      if (this.gridApi) {
        return (
          this.gridApi.paginationGetCurrentPage() + 1 ===
          this.gridApi.paginationGetTotalPages()
        )
      }
      return true
    },
  },

  watch: {
    pageSizeSelected(newValue) {
      this.gridApi.paginationSetPageSize(newValue)
    },
  },

  methods: {
    handleFirstPage() {
      this.gridApi.paginationGoToFirstPage()
    },

    handlePreviousPage() {
      this.gridApi.paginationGoToPreviousPage()
    },

    handleNextPage() {
      this.gridApi.paginationGoToNextPage()
    },

    handleLastPage() {
      this.gridApi.paginationGoToLastPage()
    },
  },
}
</script>

<style lang="scss">
.asc-grid-pagination {
  margin-top: 10px;
  font-size: 14px;
  display: flex;

  & .asc-grid-pagination--container {
    flex: 1;
    justify-content: flex-end;
    display: flex;

    & .asc-paging-page-size-panel {
      display: flex;

      .asc-autocomplete-selector--selector {
        margin: -2px;
      }

      & .asc-paging-page-size--label {
        margin-right: 8px;
      }
    }

    & .asc-paging-row-summary-panel {
      margin-left: 16px;
    }

    & .asc-paging-page-summary-panel {
      margin-left: 16px;
      display: flex;

      & .asc-paging-page-summary {
        margin-right: 8px;
      }

      & .asc-paging-button {
        & .v-btn {
          max-height: 18px;
          max-width: 18px;
          margin: 0 8px 0 0;

          & .v-icon {
            font-weight: bold;
          }

          &:hover {
            color: $accent-600;
          }

          &::before {
            background-color: transparent !important;
          }
        }
      }
    }
  }
}

@media (max-width: 600px) {
  .asc-grid-pagination {
    & .asc-grid-pagination--container {
      display: flex;
      flex-wrap: wrap;

      & .asc-paging-page-size-panel {
        order: 2;
        width: 100%;
        margin: 12px 8px 0 0;
        justify-content: flex-end;
      }
    }
  }
}
</style>
